import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getUserById, updateUserData } from '../firebase/helpers';
import bcrypt from 'bcryptjs';
import { validateWalletAddress } from '../utils/walletValidation';
import { getWithdrawalLimits } from './withdrawalLimitService';

/**
 * Withdrawal statuses
 */
export const WITHDRAWAL_STATUS = {
  UNDER_REVIEW: 'Under Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected'
};

/**
 * Payment networks
 */
export const PAYMENT_NETWORKS = {
  TRON_TRC20: 'TRON_TRC20',
  ETHEREUM_ERC20: 'ETHEREUM_ERC20',
  BTC_BLOCKCHAIN: 'BTC_BLOCKCHAIN'
};

/**
 * Withdrawal limits (fallback defaults)
 * Note: Actual limits are fetched from Firestore via getWithdrawalLimits()
 */
export const WITHDRAWAL_LIMITS = {
  MIN_AMOUNT: 100, // Minimum withdrawal amount in USDT (fixed at $100)
  MAX_AMOUNT: 10000, // Maximum withdrawal amount in USDT (configurable)
  MAX_DAILY_AMOUNT: 10000, // Maximum daily withdrawal per user
  MAX_ATTEMPTS: 5, // Maximum transaction password attempts before lockout
  LOCKOUT_DURATION: 15 * 60 * 1000 // 15 minutes in milliseconds
};

/**
 * Verify transaction password
 */
export const verifyTransactionPassword = async (userId, password) => {
  try {
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Check if user has transaction password set
    if (!user.transactionPassword) {
      throw new Error('Transaction password not set. Please set it in your account settings.');
    }

    // Check for lockout
    if (user.transactionPasswordLockoutUntil) {
      const lockoutUntil = user.transactionPasswordLockoutUntil.toDate ? 
        user.transactionPasswordLockoutUntil.toDate() : 
        new Date(user.transactionPasswordLockoutUntil);
      
      if (lockoutUntil > new Date()) {
        const minutesLeft = Math.ceil((lockoutUntil - new Date()) / 60000);
        throw new Error(`Account locked due to too many failed attempts. Please try again in ${minutesLeft} minute(s).`);
      } else {
        // Lockout expired, reset attempts
        await updateUserData(userId, {
          transactionPasswordAttempts: 0,
          transactionPasswordLockoutUntil: null
        });
      }
    }

    // Verify password
    const isValid = await bcrypt.compare(password, user.transactionPassword);
    
    if (!isValid) {
      // Increment failed attempts
      const attempts = (user.transactionPasswordAttempts || 0) + 1;
      const updates = { transactionPasswordAttempts: attempts };

      // Lock account if max attempts reached
      if (attempts >= WITHDRAWAL_LIMITS.MAX_ATTEMPTS) {
        const lockoutUntil = new Date(Date.now() + WITHDRAWAL_LIMITS.LOCKOUT_DURATION);
        updates.transactionPasswordLockoutUntil = Timestamp.fromDate(lockoutUntil);
      }

      await updateUserData(userId, updates);

      const remainingAttempts = WITHDRAWAL_LIMITS.MAX_ATTEMPTS - attempts;
      if (remainingAttempts > 0) {
        throw new Error(`Invalid transaction password. ${remainingAttempts} attempt(s) remaining.`);
      } else {
        throw new Error(`Invalid transaction password. Account locked for 15 minutes.`);
      }
    }

    // Reset attempts on successful verification
    if (user.transactionPasswordAttempts > 0) {
      await updateUserData(userId, {
        transactionPasswordAttempts: 0,
        transactionPasswordLockoutUntil: null
      });
    }

    return true;
  } catch (error) {
    console.error('Error verifying transaction password:', error);
    throw error;
  }
};

/**
 * Check daily withdrawal limit
 */
export const checkDailyWithdrawalLimit = async (userId, amount) => {
  try {
    // Get current limits from Firestore
    const limits = await getWithdrawalLimits();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStart = Timestamp.fromDate(today);
    const todayEnd = Timestamp.fromDate(new Date(today.getTime() + 24 * 60 * 60 * 1000));

    // Fetch all approved withdrawals for user and filter client-side to avoid index requirement
    const withdrawalsRef = collection(db, 'withdrawals');
    const q = query(
      withdrawalsRef,
      where('userId', '==', userId),
      where('status', '==', WITHDRAWAL_STATUS.APPROVED)
    );

    const snapshot = await getDocs(q);
    let totalToday = 0;
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
      
      // Filter by date client-side
      if (createdAt >= today && createdAt < todayEnd) {
        totalToday += data.amount || 0;
      }
    });

    const maxDailyAmount = limits.maxDailyAmount || WITHDRAWAL_LIMITS.MAX_DAILY_AMOUNT;
    if (totalToday + amount > maxDailyAmount) {
      const remaining = maxDailyAmount - totalToday;
      throw new Error(`Daily withdrawal limit exceeded. You can withdraw up to ${remaining.toFixed(2)} USDT today.`);
    }

    return { totalToday, remaining: maxDailyAmount - totalToday };
  } catch (error) {
    console.error('Error checking daily withdrawal limit:', error);
    throw error;
  }
};

/**
 * Create withdrawal request
 */
export const createWithdrawalRequest = async (userId, withdrawalData) => {
  try {
    const { amount, transactionPassword, paymentNetwork, walletAddress } = withdrawalData;

    // Get current limits from Firestore
    const limits = await getWithdrawalLimits();
    const minAmount = limits.minAmount || WITHDRAWAL_LIMITS.MIN_AMOUNT;
    const maxAmount = limits.maxAmount || WITHDRAWAL_LIMITS.MAX_AMOUNT;

    // Validate amount - minimum is $100
    if (!amount || amount < minAmount) {
      throw new Error(`Withdrawal amount must be at least $${minAmount}. Please enter a valid amount above $${minAmount}.`);
    }

    // Validate maximum amount
    if (amount > maxAmount) {
      throw new Error(`Maximum withdrawal amount is $${maxAmount} USDT`);
    }

    // Verify transaction password
    await verifyTransactionPassword(userId, transactionPassword);

    // Get user and check balance
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Check for negative balance
    if (user.hasNegativeBalance || (user.balances?.negative && user.balances.negative < 0)) {
      throw new Error('Account restricted: You have a negative balance. Please contact customer service to resolve this issue.');
    }

    const availableBalance = user.balances?.wallet || 0;
    if (amount > availableBalance) {
      throw new Error(`Insufficient balance. Available: ${availableBalance.toFixed(2)} USDT`);
    }

    // Check daily withdrawal limit
    await checkDailyWithdrawalLimit(userId, amount);

    // Validate wallet address
    const addressValidation = validateWalletAddress(walletAddress, paymentNetwork);
    if (!addressValidation.valid) {
      throw new Error(addressValidation.error);
    }

    // Create withdrawal request
    const withdrawalRef = doc(collection(db, 'withdrawals'));
    const withdrawalId = withdrawalRef.id;

    const withdrawal = {
      id: withdrawalId,
      userId,
      userEmail: user.email,
      username: user.username,
      amount: parseFloat(amount),
      paymentNetwork,
      walletAddress: walletAddress.trim(),
      status: WITHDRAWAL_STATUS.UNDER_REVIEW,
      transactionHash: null,
      adminNotes: null,
      rejectionReason: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      processedAt: null,
      processedBy: null
    };

    await setDoc(withdrawalRef, withdrawal);

    // Hold balance (move from wallet to frozen)
    const currentWallet = user.balances?.wallet || 0;
    const currentFrozen = user.balances?.frozen || 0;
    const currentTotal = user.balances?.total || 0;

    await updateUserData(userId, {
      balances: {
        ...user.balances,
        wallet: currentWallet - amount,
        frozen: currentFrozen + amount,
        total: currentTotal // Total remains the same, just reallocated
      }
    });

    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId,
        type: 'info',
        title: 'Withdrawal Request Submitted',
        message: `Your withdrawal request of ${amount} USDT is under review. You will receive your funds within an hour.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
      // Don't fail withdrawal if notification fails
    }

    return withdrawal;
  } catch (error) {
    console.error('Error creating withdrawal request:', error);
    throw error;
  }
};

/**
 * Get user withdrawal history
 */
export const getUserWithdrawals = async (userId, limitCount = 50) => {
  try {
    // Fetch all withdrawals for user and sort client-side to avoid index requirement
    const withdrawalsRef = collection(db, 'withdrawals');
    const q = query(
      withdrawalsRef,
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);
    const withdrawals = [];
    snapshot.forEach((doc) => {
      withdrawals.push({ id: doc.id, ...doc.data() });
    });

    // Sort by createdAt descending (most recent first)
    withdrawals.sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 
                   (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 
                   (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return bTime - aTime; // Descending order
    });

    // Apply limit after sorting
    return withdrawals.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting user withdrawals:', error);
    throw error;
  }
};

/**
 * Get withdrawal by ID
 */
export const getWithdrawalById = async (withdrawalId) => {
  try {
    const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
    const snapshot = await getDoc(withdrawalRef);
    
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting withdrawal:', error);
    throw error;
  }
};

/**
 * Get all withdrawals (admin only)
 */
export const getAllWithdrawals = async (filters = {}) => {
  try {
    // If filtering by status, we need to fetch all and filter client-side
    // to avoid requiring a composite index
    let q;
    
    if (filters.status) {
      // Filter by status only (no orderBy to avoid index requirement)
      q = query(collection(db, 'withdrawals'), where('status', '==', filters.status));
    } else if (filters.userId) {
      // Filter by userId only
      q = query(collection(db, 'withdrawals'), where('userId', '==', filters.userId));
    } else {
      // No filters, can use orderBy
      q = query(collection(db, 'withdrawals'), orderBy('createdAt', 'desc'));
    }

    if (filters.limit && !filters.status && !filters.userId) {
      q = query(q, limit(filters.limit));
    }

    const snapshot = await getDocs(q);
    const withdrawals = [];
    snapshot.forEach((doc) => {
      withdrawals.push({ id: doc.id, ...doc.data() });
    });

    // Sort client-side if we filtered (to avoid index requirement)
    if (filters.status || filters.userId) {
      withdrawals.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime; // Descending order
      });
      
      // Apply limit after sorting if specified
      if (filters.limit) {
        return withdrawals.slice(0, filters.limit);
      }
    }

    return withdrawals;
  } catch (error) {
    console.error('Error getting all withdrawals:', error);
    throw error;
  }
};

/**
 * Approve withdrawal (admin only)
 */
export const approveWithdrawal = async (withdrawalId, transactionHash, adminId, adminEmail) => {
  try {
    const withdrawal = await getWithdrawalById(withdrawalId);
    if (!withdrawal) {
      throw new Error('Withdrawal not found');
    }

    if (withdrawal.status !== WITHDRAWAL_STATUS.UNDER_REVIEW) {
      throw new Error(`Cannot approve withdrawal with status: ${withdrawal.status}`);
    }

    if (!transactionHash || transactionHash.trim() === '') {
      throw new Error('Transaction hash is required');
    }

    // Update withdrawal status
    const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
    await updateDoc(withdrawalRef, {
      status: WITHDRAWAL_STATUS.APPROVED,
      transactionHash: transactionHash.trim(),
      processedAt: Timestamp.now(),
      processedBy: adminEmail,
      updatedAt: Timestamp.now()
    });

    // Release frozen balance (remove from frozen, total already adjusted)
    const user = await getUserById(withdrawal.userId);
    if (user) {
      const currentFrozen = user.balances?.frozen || 0;
      const newFrozen = Math.max(0, currentFrozen - withdrawal.amount);

      await updateUserData(withdrawal.userId, {
        balances: {
          ...user.balances,
          frozen: newFrozen
        }
      });
    }

    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: withdrawal.userId,
        type: 'success',
        title: 'Withdrawal Approved',
        message: `Your withdrawal of ${withdrawal.amount} USDT has been approved. Transaction hash: ${transactionHash}`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }

    return { success: true, withdrawal: { ...withdrawal, status: WITHDRAWAL_STATUS.APPROVED } };
  } catch (error) {
    console.error('Error approving withdrawal:', error);
    throw error;
  }
};

/**
 * Reject withdrawal (admin only)
 */
export const rejectWithdrawal = async (withdrawalId, rejectionReason, adminId, adminEmail) => {
  try {
    const withdrawal = await getWithdrawalById(withdrawalId);
    if (!withdrawal) {
      throw new Error('Withdrawal not found');
    }

    if (withdrawal.status !== WITHDRAWAL_STATUS.UNDER_REVIEW) {
      throw new Error(`Cannot reject withdrawal with status: ${withdrawal.status}`);
    }

    if (!rejectionReason || rejectionReason.trim() === '') {
      throw new Error('Rejection reason is required');
    }

    // Update withdrawal status
    const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
    await updateDoc(withdrawalRef, {
      status: WITHDRAWAL_STATUS.REJECTED,
      rejectionReason: rejectionReason.trim(),
      processedAt: Timestamp.now(),
      processedBy: adminEmail,
      updatedAt: Timestamp.now()
    });

    // Refund balance (move from frozen back to wallet)
    const user = await getUserById(withdrawal.userId);
    if (user) {
      const currentWallet = user.balances?.wallet || 0;
      const currentFrozen = user.balances?.frozen || 0;
      const currentTotal = user.balances?.total || 0;

      await updateUserData(withdrawal.userId, {
        balances: {
          ...user.balances,
          wallet: currentWallet + withdrawal.amount,
          frozen: Math.max(0, currentFrozen - withdrawal.amount),
          total: currentTotal
        }
      });
    }

    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: withdrawal.userId,
        type: 'warning',
        title: 'Withdrawal Rejected',
        message: `Your withdrawal of ${withdrawal.amount} USDT has been rejected. Reason: ${rejectionReason}`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }

    return { success: true, withdrawal: { ...withdrawal, status: WITHDRAWAL_STATUS.REJECTED } };
  } catch (error) {
    console.error('Error rejecting withdrawal:', error);
    throw error;
  }
};

